import { memo } from 'react';

import { splitTickTextIntoLines } from '../../model/lib/splitTickTextIntoLines';

import type { SplitTickTextResult } from '../../model/lib/splitTickTextIntoLines';

interface TickDataPoint {
    name?: string;
}

interface WrappedTickProps {
    x?: number;
    y?: number;
    payload?: { value?: string | number };
    index?: number;
    data: TickDataPoint[];
    formatter?: (
        label: string | number,
        index: number,
        data: TickDataPoint[],
    ) => string;
    precomputedTicks?: Array<{
        formatted: string;
        split: SplitTickTextResult;
    }>;
    angle?: number;
    maxLines?: number;
    maxCharsPerLine?: number;
}

export const ReportWidgetXAxisWrappedTick = memo(
    ({
        x,
        y,
        payload,
        index = 0,
        data,
        formatter,
        precomputedTicks,
        angle = 0,
        maxLines = 2,
        maxCharsPerLine = 24,
    }: WrappedTickProps) => {
        if (typeof x !== 'number' || typeof y !== 'number') {
            return null;
        }

        const lineHeight = 16;
        const rawValue = payload?.value ?? '';
        const precomputedTick = precomputedTicks?.[index];
        const formatted =
            precomputedTick?.formatted ??
            (formatter ? formatter(rawValue, index, data) : String(rawValue));
        const { lines, wasTruncated } =
            precomputedTick?.split ??
            splitTickTextIntoLines(formatted, maxLines, maxCharsPerLine);

        return (
            <text
                x={x}
                y={y}
                textAnchor={angle === 0 ? 'middle' : 'end'}
                fontSize={12}
                transform={
                    angle === 0 ? undefined : `rotate(${angle}, ${x}, ${y})`
                }
            >
                {wasTruncated && <title>{formatted}</title>}
                {lines.map((line, lineIndex) => (
                    <tspan
                        key={`${line}-${lineIndex}`}
                        x={x}
                        dy={lineIndex === 0 ? 12 : lineHeight}
                    >
                        {line}
                    </tspan>
                ))}
            </text>
        );
    },
);

ReportWidgetXAxisWrappedTick.displayName = 'ReportWidgetXAxisWrappedTick';
